import React, { useMemo } from 'react';
import { CafClusterListItem, CafCorpusEntry, CafLooksLike } from '../../../api/cafCorpus';
import { ExampleAction, ExamplesTable } from './ExamplesTable';
import { QueueTable } from './QueueTable';
import { LenderOf, lenderName } from './teachFilter';

interface Props {
  category?: 'queue' | 'approved' | 'off';
  queue: CafClusterListItem[];
  examples: CafCorpusEntry[];
  lenderOf: LenderOf;
  canSwitchOn: boolean;
  /** null once every page of the selected example category has loaded. */
  examplesNext: number | null;
  /** null once every page of the queue has loaded. */
  queueCursor: string | null;
  onOpenGroup: (id: string) => void;
  /** #705: open a group with the label of the approved example it looks like filled in. */
  onLabelLike?: (id: string, like: CafLooksLike) => void;
  onOpenExample: (entry: CafCorpusEntry) => void;
  onAction: (action: ExampleAction, entry: CafCorpusEntry) => void;
  onMoreExamples: () => void;
  onMoreQueue: () => void;
}

interface Card {
  name: string;
  queue: CafClusterListItem[];
  examples: CafCorpusEntry[];
}

export const ByLenderView: React.FC<Props> = ({
  category = 'queue', queue, examples, lenderOf, canSwitchOn, examplesNext, queueCursor, onOpenGroup, onLabelLike, onOpenExample, onAction,
  onMoreExamples, onMoreQueue,
}) => {
  const allPagesLoaded = examplesNext === null;
  const moreQueueAvailable = queueCursor !== null;

  // One pass over each list (not one pass per lender): bucket rows into cards as we go.
  const cards = useMemo(() => {
    const byName = new Map<string, Card>();
    const bucket = (name: string): Card => {
      const existing = byName.get(name);
      if (existing) return existing;
      const created: Card = { name, queue: [], examples: [] };
      byName.set(name, created);
      return created;
    };
    queue.forEach((group) => bucket(lenderName(group.sender, lenderOf)).queue.push(group));
    examples.forEach((entry) => bucket(lenderName(entry.sender, lenderOf)).examples.push(entry));
    return Array.from(byName.values())
      .sort((a, b) => (b.queue.length - a.queue.length) || a.name.localeCompare(b.name));
  }, [queue, examples, lenderOf]);

  return (
    <>
      {cards.length
        ? (
          <div className="cur-lender-grid">
            {cards.map((card) => (
              <section key={card.name} className="ui-card cur-lender-card" aria-label={card.name}>
                <div className="ui-card__header">
                  <h2 className="ui-card__title" style={{ fontSize: 15 }}>{card.name}</h2>
                  <div className="cur-lender-meta">
                    <span>{category === 'queue' ? `${card.queue.length} to label` : `${card.examples.length} ${category === 'off' ? 'switched off' : 'approved'}`}</span>
                  </div>
                </div>
                <div className="ui-card__body">
                  {category === 'queue' ? <><div className="cur-sub-h">To label</div>
                  <QueueTable rows={card.queue} compact lenderOf={lenderOf} ariaLabel={`Groups to label — ${card.name}`}
                    onOpen={onOpenGroup} onLabelLike={onLabelLike} empty="Nothing to label here." />
                  </> : <><div className="cur-sub-h">{category === 'off' ? 'Switched off' : 'Approved examples'}</div>
                  <ExamplesTable rows={card.examples} compact lenderOf={lenderOf} canSwitchOn={canSwitchOn}
                    ariaLabel={`${category === 'off' ? 'Switched off' : 'Approved examples'} — ${card.name}`}
                    onOpen={onOpenExample} onAction={onAction} empty="" /></>}

                </div>
              </section>
            ))}
          </div>
        )
        : <div className="cur-empty"><strong>{!allPagesLoaded || moreQueueAvailable
          ? 'No matches on the pages loaded so far.' : 'Nothing here.'}</strong>
          {allPagesLoaded && !moreQueueAvailable && 'Try a different search or lender.'}</div>}
      {(!allPagesLoaded || moreQueueAvailable) && (
        <div className="cur-more-row">
          {moreQueueAvailable && (
            <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" onClick={onMoreQueue}>Show more groups</button>
          )}
          {!allPagesLoaded && (
            <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" onClick={onMoreExamples}>Show more</button>
          )}
        </div>
      )}
    </>
  );
};
